import { mergeAttributes, Node } from '@tiptap/core';
import { convertToPt } from '../extension-word/utils';

export interface TableCellOptions {
    HTMLAttributes: Record<string, any>;
}

export const TableCell = Node.create<TableCellOptions>({
    name: 'tableCell',

    addOptions() {
        return {
            HTMLAttributes: {
                class: 'relative border border-black',
            },
        };
    },

    content: 'block+',
    tableRole: 'wordCell',
    isolating: true,
    group: 'tableCell',
    allowGapCursor: false,

    addAttributes() {
        return {
            colspan: {
                default: 1,
            },
            rowspan: {
                default: 1,
            },
            verticalAlignment: {
                default: 'top',
                parseHTML: element => {
                    return element.dataset.verticalAlignment || 'top';
                },
                renderHTML: attributes => {
                    return {
                        style: `vertical-align: ${attributes.verticalAlignment};`,
                        ...(attributes.verticalAlignment ? { 'data-vertical-alignment': attributes.verticalAlignment } : {}),
                    };
                },
            },
            backgroundColor: {
                default: null,
                parseHTML: element => {
                    return element.dataset.backgroundColor || null;
                },
                renderHTML: attributes => {
                    return {
                        style: `background-color: ${attributes.backgroundColor || 'transparent'};`,
                        ...(attributes.backgroundColor ? { 'data-background-color': attributes.backgroundColor } : {}),
                    };
                },
            },
            // ooxml기준 <w:tblW w:w="1000" w:type="dxa"/> 의 pt 버전 {value: 0, type: 'POINT'}
            cellWidth: {
                default: null,
                parseHTML: element => {
                    return JSON.parse(element.dataset.width || 'null');
                },
                renderHTML: attributes => {
                    return {
                        ...(attributes.cellWidth ? { 'data-width': JSON.stringify(attributes.cellWidth) } : {}),
                    };
                },
            },
            padding: {
                default: {
                    top: '0pt',
                    right: '5.4pt',
                    bottom: '0pt',
                    left: '5.4pt',
                },
                parseHTML: element => {
                    return element.dataset.padding
                        ? JSON.parse(element.dataset.padding)
                        : {
                              top: '0pt',
                              right: '5.4pt',
                              bottom: '0pt',
                              left: '5.4pt',
                          };
                },
                renderHTML: attributes => {
                    const padding = attributes.padding;
                    const top = Math.max(0, convertToPt(padding.top) - (attributes.borders?.top?.lineWidth || 0)) + 'pt';
                    const right = Math.max(0, convertToPt(padding.right) - (attributes.borders?.right?.lineWidth || 0)) + 'pt';
                    const bottom = Math.max(0, convertToPt(padding.bottom) - (attributes.borders?.bottom?.lineWidth || 0)) + 'pt';
                    const left = Math.max(0, convertToPt(padding.left) - (attributes.borders?.left?.lineWidth || 0)) + 'pt';
                    return {
                        style: `padding: ${top} ${right} ${bottom} ${left};`,
                        ...(attributes.padding ? { 'data-padding': JSON.stringify(attributes.padding) } : {}),
                    };
                },
            },
            borders: {
                default: null,
                parseHTML: element => JSON.parse(element.dataset.borders || 'null'),
                renderHTML: attributes => {
                    const { bottom, left, right, top } = attributes.borders ?? {};
                    // 너비가 워드의 기본값처럼 0.5pt 로 나와서 입력하는 경우 투명선이 0pt 로 전환되는 문제가 있어서 강제로 1px 로 변환
                    const bottomStyle = bottom
                        ? `border-bottom: ${bottom.lineWidth < 1 ? '1px' : `${Math.max(bottom.lineWidth, 1)}pt`} ${!bottom.isVisible ? 'dotted' : 'solid'} ${!bottom.isVisible ? 'var(--word-table-border-transparent-color)' : bottom.color || 'black'};`
                        : '';
                    const leftStyle = left
                        ? `border-left: ${left.lineWidth < 1 ? '1px' : `${Math.max(left.lineWidth, 1)}pt`} ${!left.isVisible ? 'dotted' : 'solid'} ${!left.isVisible ? 'var(--word-table-border-transparent-color)' : left.color || 'black'};`
                        : '';
                    const rightStyle = right
                        ? `border-right: ${right.lineWidth < 1 ? '1px' : `${Math.max(right.lineWidth, 1)}pt`} ${!right.isVisible ? 'dotted' : 'solid'} ${!right.isVisible ? 'var(--word-table-border-transparent-color)' : right.color || 'black'};`
                        : '';
                    const topStyle = top
                        ? `border-top: ${top.lineWidth < 1 ? '1px' : `${Math.max(top.lineWidth, 1)}pt`} ${!top.isVisible ? 'dotted' : 'solid'} ${!top.isVisible ? 'var(--word-table-border-transparent-color)' : top.color || 'black'};`
                        : '';

                    return {
                        style: [bottomStyle, leftStyle, rightStyle, topStyle].join(''),
                        'data-borders': JSON.stringify(attributes.borders || {}),
                    };
                },
            },
        };
    },

    parseHTML() {
        return [{ tag: 'td' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['td', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },
});
